import React, { FC, PropsWithChildren } from 'react';

import UserDisplayableError from 'app/interfaces/UserDisplayableError';
import electron from 'app/native/node/electron';
import MinDurationPromise from 'app/utils/MinDurationPromise';
import createDeferredPromise from 'app/utils/createDeferredPromise';
import _ from 'lodash';
import { useRef, useCallback, useState } from 'react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { WithRouterProps, withRouter } from 'react-router';
import { Button, Icon, Modal, List, Label, Popup } from 'semantic-ui-react';
import { DataLossGuardConnectorContext } from './DataLossGuardConnectorContext';
import AllSettledObject from 'app/utils/async/AllSettledObject';

export type DataLossGuardProps = WithRouterProps & {};

type SimpleCallback = () => void;

type PromiseCallback = () => Promise<void>;

// Add consolidated task state type
type TaskState = {
  saveCallback: PromiseCallback;
  info?: DataLossGuardConnector.TaskInfo;
};

type TasksMap = { [key: string]: TaskState | undefined };

type ErrorRejectionDetail = {
  message: string;
};

type DataLossGuardImplProps = DataLossGuardProps & { resetState: () => void };

const DataLossGuardImpl: FC<React.PropsWithChildren<DataLossGuardImplProps>> = ({
  children,
  router,
  routes,
  resetState,
}) => {
  const intl = useIntl();
  const [saveOngoing, setSaveOngoing] = useState(false);
  const [failedSaveRequests, setFailedSaveRequests] = useState<Record<string, string>>(undefined);

  const [tasks, setTasks] = useState<TasksMap>({});
  const [isSlowSaveOngoing, setIsSlowSaveOngoing] = useState<boolean>(undefined);

  const saveAllDataFn = useRef<() => Promise<void>>();
  const componentClearPromise = useRef<DeferredPromise<void>>();

  const actionOnLeave = useRef<SimpleCallback>();
  const cleanupExitHooks = useRef<SimpleCallback>();
  const exitFn = useRef<SimpleCallback>();
  const setDataSaveCallbackDisabled = useRef<boolean>(false);

  const tasksRef = useRef<TasksMap>({});

  const dataLossGuardConnector = useRef<DataLossGuardConnector>(
    {} as unknown as DataLossGuardConnector
  );

  exitFn.current = () => {
    setDataSaveCallbackDisabled.current = true;
    cleanupExitHooks.current?.();
    cleanupExitHooks.current = undefined;
    actionOnLeave.current?.();
  };

  saveAllDataFn.current = async () => {
    setSaveOngoing(true);

    const slowSaveTimeout = setTimeout(() => {
      setIsSlowSaveOngoing(true);
    }, 10000);

    const tasksPromises = _.mapValues(tasksRef.current, (task, saveKey) =>
      task?.saveCallback?.()?.catch((error) => {
        throw {
          message: error instanceof UserDisplayableError ? error.message : saveKey,
        } as ErrorRejectionDetail;
      })
    );

    const requestsResults = await (failedSaveRequests
      ? MinDurationPromise(AllSettledObject(tasksPromises), 1000)
      : AllSettledObject(tasksPromises));

    clearTimeout(slowSaveTimeout);

    setSaveOngoing(false);
    if (_.every(requestsResults, { status: 'fulfilled' })) {
      setFailedSaveRequests(undefined);
      exitFn.current?.();
      componentClearPromise.current?.resolve();
    } else {
      setFailedSaveRequests(
        _.mapValues(
          _.omitBy(requestsResults, { status: 'fulfilled' }),
          ({ reason }: PromiseRejectedResult) => (reason as ErrorRejectionDetail).message
        )
      );
    }
  };

  const retry = useCallback(() => saveAllDataFn.current(), []);

  const cancel = useCallback(() => {
    setFailedSaveRequests(undefined);
    setIsSlowSaveOngoing(false);
    componentClearPromise.current?.reject();
    componentClearPromise.current = undefined;
    actionOnLeave.current = undefined;
  }, []);

  const leavePage = useCallback(() => {
    const ongoingTasks = _.omit(
      _.pickBy(tasksRef.current, (task) => task.saveCallback !== undefined),
      Object.keys(failedSaveRequests || {})
    );

    const isAnyMandatoryOngoingTasks = _.some(ongoingTasks, (task) => !task?.info?.onCancel);
    if (isAnyMandatoryOngoingTasks) return;

    const cancelableOngoingTasks = _.pickBy(ongoingTasks, (task) => task?.info?.onCancel);

    _.forEach(cancelableOngoingTasks, (task) => {
      task.info?.onCancel?.();
    });

    setFailedSaveRequests(undefined);
    exitFn.current?.();
    resetState();
  }, [failedSaveRequests, setFailedSaveRequests, resetState]);

  const registerExitHooksIfNeeded = (currentTasks: TasksMap) => {
    if (_.every(currentTasks, (task) => task?.saveCallback === undefined)) {
      return undefined;
    }

    const unbindHook = router?.setRouteLeaveHook(_.last(routes), (nextRoute) => {
      actionOnLeave.current = () => router.push(nextRoute);

      saveAllDataFn.current();
      return false;
    });

    const beforeUnloadCallback = (evt: BeforeUnloadEvent) => {
      evt.preventDefault();
      evt.returnValue = intl.formatMessage({ id: 'dropzone.get_out_without_save' });

      actionOnLeave.current = () => {
        // We cannot control the window as finely when using the browser.
        electron()?.remote.getCurrentWindow().close();
      };
      saveAllDataFn.current();
      return evt.returnValue;
    };
    window.addEventListener('beforeunload', beforeUnloadCallback);

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadCallback);
      unbindHook();
    };
  };

  dataLossGuardConnector.current.setDataSaveCallback = (
    key: string,
    saveCallback: PromiseCallback,
    taskInfo?: DataLossGuardConnector.TaskInfo
  ) => {
    if (setDataSaveCallbackDisabled.current) return;

    cleanupExitHooks.current?.();
    tasksRef.current = {
      ...tasksRef.current,
      [key]: { saveCallback, info: taskInfo },
    };
    cleanupExitHooks.current = registerExitHooksIfNeeded(tasksRef.current);
    setTasks(tasksRef.current);
  };

  dataLossGuardConnector.current.clearComponentWithDataSave = async () => {
    actionOnLeave.current = resetState;
    componentClearPromise.current ??= createDeferredPromise();
    saveAllDataFn.current();
    return componentClearPromise.current;
  };

  const triggerExitOnLastDataSave = () => {
    if (_.some(tasks, (task) => task?.saveCallback !== undefined)) return;

    const isDataLossGuardActive = failedSaveRequests !== undefined;
    if (!isDataLossGuardActive) return;

    setFailedSaveRequests(undefined);
    exitFn.current?.();
    componentClearPromise.current?.resolve();
  };
  // We trigger this inside an effect instead of during the setCallback to only run final save
  // once user has cleared all callbacks.
  useEffect(triggerExitOnLastDataSave, [tasks, failedSaveRequests]);

  // Derive ongoing tasks from consolidated state
  const ongoingTasks = _.omit(
    _.pickBy(tasks, (task) => task.saveCallback !== undefined),
    Object.keys(failedSaveRequests || {})
  );
  const mandatoryOngoingTasks = _.pickBy(ongoingTasks, (task) => !task?.info?.onCancel);
  const cancelableOngoingTasks = _.pickBy(ongoingTasks, (task) => task?.info?.onCancel);
  const isCancelable = _.isEmpty(mandatoryOngoingTasks);

  const isShowingSlowRequests = isSlowSaveOngoing && failedSaveRequests === undefined;

  return (
    <DataLossGuardConnectorContext.Provider value={dataLossGuardConnector.current}>
      {children}

      <Modal
        open={isSlowSaveOngoing || failedSaveRequests !== undefined}
        closeIcon
        onClose={cancel}
        centered={false}
        dimmer={
          <Modal.Dimmer
            style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
            data-testid="dataloss-guard"
          />
        }
      >
        <Modal.Header>
          {isShowingSlowRequests ? (
            <FormattedMessage id="dropzone.unsaved_data_modal.slow.header" />
          ) : (
            <FormattedMessage id="dropzone.unsaved_data_modal.header" />
          )}
        </Modal.Header>
        <Modal.Content>
          {isShowingSlowRequests ? (
            <FormattedMessage id="dropzone.unsaved_data_modal.slow.content" />
          ) : (
            <FormattedMessage id="dropzone.unsaved_data_modal.content" />
          )}
          <br />

          {!_.isEmpty(failedSaveRequests) && (
            <>
              <h3>
                <FormattedMessage id="dropzone.unsaved_data_modal.error_list" />
              </h3>
              <List>
                {_.map(failedSaveRequests, (errorMessage, saveKey) => (
                  <List.Item key={saveKey}>
                    <Label color="red" horizontal>
                      <Icon name="warning sign" />
                      {tasks[saveKey]?.info?.taskName}
                    </Label>
                    {errorMessage}
                  </List.Item>
                ))}
              </List>
            </>
          )}
          {!_.isEmpty(mandatoryOngoingTasks) && (
            <>
              <h3>
                <FormattedMessage id="dropzone.unsaved_data_modal.mandatory_ongoing_list" />
              </h3>
              <List>
                {_.map(mandatoryOngoingTasks, (errorMessage, saveKey) => (
                  <List.Item key={saveKey}>
                    <Label color="orange" horizontal>
                      <Icon name="sync" loading />
                      {tasks[saveKey]?.info?.taskName}
                    </Label>
                  </List.Item>
                ))}
              </List>
            </>
          )}
          {!_.isEmpty(cancelableOngoingTasks) && (
            <>
              <h3>
                <FormattedMessage id="dropzone.unsaved_data_modal.cancelable_ongoing_list" />
              </h3>
              <List>
                {_.map(cancelableOngoingTasks, (errorMessage, saveKey) => (
                  <List.Item key={saveKey}>
                    <Label color="blue" horizontal>
                      <Icon name="sync" loading />
                      {tasks[saveKey]?.info?.taskName}
                    </Label>
                    {tasks[saveKey]?.info?.cancellationWarning ?? (
                      <FormattedMessage id="dropzone.unsaved_data_modal.cancellable_no_warning" />
                    )}
                  </List.Item>
                ))}
              </List>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button primary onClick={retry} style={saveOngoing ? { pointerEvent: 'none' } : {}}>
            {saveOngoing && <Icon name="sync" data-testid="retry-ongoing" loading />}
            <FormattedMessage id="dropzone.unsaved_data_modal.retry" />
          </Button>
          <Button onClick={leavePage} disabled={!isCancelable} secondary>
            <FormattedMessage id="dropzone.unsaved_data_modal.leave_page" />
          </Button>
        </Modal.Actions>
      </Modal>
    </DataLossGuardConnectorContext.Provider>
  );
};

function DataLossGuard({ children, ...props }: PropsWithChildren<DataLossGuardProps>) {
  const [resetKey, setResetKey] = useState(0);
  const resetState = useCallback(() => {
    setResetKey((prevResetKey) => prevResetKey + 1);
  }, []);

  return (
    <DataLossGuardImpl {...props} resetState={resetState} key={resetKey}>
      {children}
    </DataLossGuardImpl>
  );
}

export default withRouter(DataLossGuard);
